import type { Question } from '../../types';

// ---------------------------------------------------------------------------
// Restaurant / Food Service Supplement
// Conditioned on industry_type eq 'restaurant'
// ---------------------------------------------------------------------------

const C = [{ field: 'industry_type', operator: 'eq' as const, value: 'restaurant' }];

export const restaurantQuestions: Question[] = [
  {
    id: 'rest_seating_capacity',
    text: 'Total seating capacity',
    section: 'supplement',
    group: 'restaurant_operations',
    sort_order: 10,
    field_type: 'number',
    placeholder: '60',
    required: true,
    validation: { min: 0, max: 2000 },
    field_path: 'supplement.restaurant.seating_capacity',
    conditions: C,
    tags: ['supplement', 'restaurant'],
  },
  {
    id: 'rest_annual_sales',
    text: 'Annual gross sales / receipts',
    section: 'supplement',
    group: 'restaurant_financials',
    sort_order: 20,
    field_type: 'currency',
    prefix: '$',
    placeholder: '750000',
    required: true,
    field_path: 'supplement.restaurant.annual_sales',
    conditions: C,
    tags: ['supplement', 'restaurant'],
  },
  {
    id: 'rest_alcohol_sales_pct',
    text: 'What percentage of sales comes from alcohol?',
    subtext: 'Enter 0 if you do not serve alcohol.',
    section: 'supplement',
    group: 'restaurant_operations',
    sort_order: 30,
    field_type: 'percentage',
    placeholder: '20',
    required: true,
    validation: { min: 0, max: 100 },
    field_path: 'supplement.restaurant.alcohol_sales_pct',
    conditions: C,
    tags: ['supplement', 'restaurant', 'liquor'],
  },
  {
    id: 'rest_seating_type',
    text: 'Service style',
    section: 'supplement',
    group: 'restaurant_operations',
    sort_order: 40,
    field_type: 'select',
    required: true,
    options: [
      { value: 'full_service', label: 'Full service (table service)' },
      { value: 'counter', label: 'Counter / quick service' },
      { value: 'bar', label: 'Bar / tavern / nightclub' },
      { value: 'delivery_only', label: 'Delivery / takeout only' },
    ],
    field_path: 'supplement.restaurant.service_style',
    conditions: C,
    tags: ['supplement', 'restaurant'],
  },
  {
    id: 'rest_hours_after_midnight',
    text: 'Are you open past midnight?',
    section: 'supplement',
    group: 'restaurant_operations',
    sort_order: 50,
    field_type: 'boolean',
    required: true,
    field_path: 'supplement.restaurant.open_past_midnight',
    conditions: C,
    tags: ['supplement', 'restaurant'],
  },
  {
    id: 'rest_delivery',
    text: 'Do you provide delivery (own employees or third-party)?',
    section: 'supplement',
    group: 'restaurant_operations',
    sort_order: 60,
    field_type: 'select',
    required: true,
    options: [
      { value: 'none', label: 'No delivery' },
      { value: 'own', label: 'Own employees deliver' },
      { value: 'third_party', label: 'Third-party (DoorDash, etc.)' },
      { value: 'both', label: 'Both' },
    ],
    field_path: 'supplement.restaurant.delivery',
    conditions: C,
    tags: ['supplement', 'restaurant', 'auto'],
  },
  {
    id: 'rest_cooking_exposure',
    text: 'Do you have cooking operations that produce grease-laden vapors (frying, grilling, broiling)?',
    section: 'supplement',
    group: 'restaurant_protection',
    sort_order: 70,
    field_type: 'boolean',
    required: true,
    field_path: 'supplement.restaurant.grease_cooking',
    conditions: C,
    tags: ['supplement', 'restaurant'],
  },
  {
    id: 'rest_hood_suppression',
    text: 'Is there an automatic hood/duct fire suppression system?',
    subtext: 'Required by most carriers when cooking produces grease vapors.',
    section: 'supplement',
    group: 'restaurant_protection',
    sort_order: 80,
    field_type: 'boolean',
    required: true,
    field_path: 'supplement.restaurant.hood_suppression',
    conditions: [...C, { field: 'rest_cooking_exposure', operator: 'eq', value: true }],
    tags: ['supplement', 'restaurant'],
  },
  {
    id: 'rest_hood_cleaning',
    text: 'How often is the hood/duct system professionally cleaned?',
    section: 'supplement',
    group: 'restaurant_protection',
    sort_order: 90,
    field_type: 'select',
    required: false,
    options: [
      { value: 'monthly', label: 'Monthly' },
      { value: 'quarterly', label: 'Quarterly' },
      { value: 'semi_annual', label: 'Semi-annually' },
      { value: 'annual', label: 'Annually' },
      { value: 'never', label: 'Not on a schedule' },
    ],
    field_path: 'supplement.restaurant.hood_cleaning_frequency',
    conditions: [...C, { field: 'rest_cooking_exposure', operator: 'eq', value: true }],
    tags: ['supplement', 'restaurant'],
  },
  {
    id: 'rest_employees_ft',
    text: 'Number of full-time employees',
    section: 'supplement',
    group: 'restaurant_employees',
    sort_order: 100,
    field_type: 'number',
    placeholder: '10',
    required: true,
    validation: { min: 0, max: 1000 },
    field_path: 'supplement.restaurant.employees_ft',
    conditions: C,
    tags: ['supplement', 'restaurant'],
  },
  {
    id: 'rest_employees_pt',
    text: 'Number of part-time / seasonal employees',
    section: 'supplement',
    group: 'restaurant_employees',
    sort_order: 110,
    field_type: 'number',
    placeholder: '15',
    required: true,
    validation: { min: 0, max: 1000 },
    field_path: 'supplement.restaurant.employees_pt',
    conditions: C,
    tags: ['supplement', 'restaurant'],
  },
  {
    id: 'rest_live_entertainment',
    text: 'Do you offer live entertainment, dancing, or DJs?',
    section: 'supplement',
    group: 'restaurant_operations',
    sort_order: 120,
    field_type: 'boolean',
    required: true,
    field_path: 'supplement.restaurant.live_entertainment',
    conditions: C,
    tags: ['supplement', 'restaurant'],
  },
];
